import type { PropertyReportFieldKey, ReportField } from "@stratify/shared";
import {
  collection,
  limit,
  orderBy,
  query,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../../../config/firebase";
import { useFirestoreDocumentListener } from "../../../shared/hooks/useFirestoreDocumentListener";

interface UsePropertyReportFieldResult {
  fieldData: ReportField | null;
  isLoading: boolean;
  error: string | null;
  lastUpdatedAt: Date | null;
}

/**
 * Custom hook for fetching and listening to a specific field of the latest property report.
 *
 * @param userId - The ID of the user who owns the property
 * @param propertyId - The ID of the property to fetch the report field for
 * @param fieldKey - The key of the field to fetch (e.g., "propertyOverview.buildingName")
 * @returns The field data, loading state, error state, and the last update timestamp
 */
export function usePropertyReportField(
  userId: string,
  propertyId: string,
  fieldKey: PropertyReportFieldKey
): UsePropertyReportFieldResult {
  const [latestVersionId, setLatestVersionId] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false); // For the initial version fetch
  const [error, setError] = useState<string | null>(null);

  // Step 1: Find the latest version ID for this field
  useEffect(
    () => {
      const fetchLatestVersion = async () => {
        if (!userId || !propertyId || !fieldKey) {
          setLatestVersionId(null);
          setLastUpdatedAt(null);
          setError(null);
          return;
        }

        setIsLoadingInitial(true);
        setError(null);
        setLatestVersionId(null); // Reset while setting up listener
        setLastUpdatedAt(null); // Reset while setting up listener

        // fieldKey can contain dots, which is valid for Firestore document IDs
        const versionsRef = collection(
          db,
          `users/${userId}/properties/${propertyId}/propertyReportFields/${fieldKey}/versions`
        );
        const q = query(versionsRef, orderBy("generatedAt", "desc"), limit(1));

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            if (!snapshot.empty) {
              const latestVersionDoc = snapshot.docs[0];
              const versionData = latestVersionDoc.data();
              // Ensure generatedAt is a Firestore Timestamp and convert to Date
              const generatedAtTimestamp = versionData.generatedAt as Timestamp;
              const generatedAtDate = generatedAtTimestamp?.toDate
                ? generatedAtTimestamp.toDate()
                : null;

              if (latestVersionDoc.id && generatedAtDate) {
                setLatestVersionId(latestVersionDoc.id);
                setLastUpdatedAt(generatedAtDate);
                setError(null); // Clear any previous error
              } else {
                setLatestVersionId(null);
                setLastUpdatedAt(null);
                setError("Latest version data is incomplete.");
              }
            } else {
              setLatestVersionId(null);
              setLastUpdatedAt(null);
            }
            setIsLoadingInitial(false);
          },
          (err) => {
            setError(
              err instanceof Error
                ? err.message
                : "Failed to listen for latest report field version"
            );
            setLatestVersionId(null);
            setLastUpdatedAt(null);
            setIsLoadingInitial(false);
          }
        );

        // Cleanup listener on unmount or when dependencies change
        return () => unsubscribe();
      };

      fetchLatestVersion();
    },
    [userId, propertyId, fieldKey] as const
  );

  // Step 2: Listen to the specific field version document when we have a latestVersionId
  const fieldDocumentPath = latestVersionId
    ? `users/${userId}/properties/${propertyId}/propertyReportFields/${fieldKey}/versions/${latestVersionId}`
    : null;

  const { data: fieldDataFromListener, error: listenerError } =
    useFirestoreDocumentListener<ReportField>(fieldDocumentPath);

  // Overall loading state based on initial loading and presence of data when expected
  const isLoading =
    isLoadingInitial ||
    (latestVersionId !== null &&
      fieldDataFromListener === undefined &&
      !listenerError);

  // Update overall error state if listener has an error
  useEffect(() => {
    if (listenerError) {
      setError(listenerError);
    }
  }, [listenerError]);

  return {
    fieldData: fieldDataFromListener || null, // Ensure it's null if undefined
    isLoading,
    error,
    lastUpdatedAt,
  };
}
